<template>
  <section
    id="private-access"
    ref="sectionRoot"
    :class="sectionClass"
  >
    <div :class="outerClass">
      <div class="overflow-hidden rounded-[1.5rem] sm:rounded-[2.25rem] border border-white/8 bg-[#11100e]">
        <div class="grid min-w-0 gap-px bg-white/8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
          <div :class="introPanelClass">
            <p class="text-[0.66rem] uppercase tracking-[0.34em] text-[#f1ebdf]/72">
              {{ copy.eyebrow }}
            </p>
            <h2 :class="introTitleClass">
              {{ copy.title }}
            </h2>
            <p :class="introBodyClass">
              {{ copy.body }}
            </p>

            <aside
              v-if="hasWaitingListCount"
              class="mt-8 rounded-[1.5rem] border border-white/10 bg-white/[0.03] px-5 py-5"
              :aria-label="copy.waitingListAria"
            >
              <p class="text-[0.62rem] uppercase tracking-[0.24em] text-[#f1ebdf]/72">
                {{ copy.waitingListLabel }}
              </p>
              <p class="mt-3 font-['Playfair_Display'] text-[2.2rem] leading-[0.94] tracking-[-0.05em] text-[#f5efe4] sm:text-[2.7rem]">
                {{ formattedWaitingListCount }}
              </p>
              <p class="mt-3 max-w-[18rem] text-[0.88rem] leading-[1.62] text-[#f1ebdf]/78">
                {{ copy.waitingListText }}
              </p>
            </aside>
          </div>

          <div :class="formPanelClass">
            <div v-if="isSuccess" class="max-w-[32rem]">
              <p class="text-[0.66rem] uppercase tracking-[0.32em] text-black/70">
                {{ copy.successEyebrow }}
              </p>
              <h3 :class="formTitleClass">
                {{ copy.successTitle }}
              </h3>
              <p :class="formBodyClass">
                {{ copy.successBody }}
              </p>
              <PrivateAccessSocialFallback variant="success" form-location="home_private_access" />
            </div>

            <form
              v-else
              ref="formRoot"
              class="max-w-[32rem]"
              @invalid.capture="trackInvalidField"
              @submit.prevent="submit"
            >
              <p class="text-[0.66rem] uppercase tracking-[0.32em] text-black/70">
                {{ copy.formEyebrow }}
              </p>
              <h3 class="mt-4 max-w-[12ch] font-['Playfair_Display'] text-[2rem] leading-[0.96] text-black sm:mt-5 sm:text-[2.9rem] sm:leading-[0.92]">
                {{ copy.formTitle }}
              </h3>
              <p class="mt-4 max-w-[18rem] text-[0.96rem] leading-[1.62] text-black/80 sm:mt-5 sm:text-[0.98rem]">
                {{ copy.formBody }}
              </p>

              <div :class="fieldsClass">
                <div class="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label class="mb-2 block whitespace-nowrap text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-first-name">
                      {{ nameCopy.firstName }}
                    </label>
                    <div class="relative">
                      <input
                        id="private-access-first-name"
                        ref="firstNameInput"
                        v-model="form.nameFirst"
                        type="text"
                        required
                        autocomplete="given-name"
                        maxlength="80"
                        class="w-full rounded-full border border-black bg-transparent px-5 py-3 pr-11 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        placeholder=" "
                        @focus="trackFieldFocus('name_first')"
                        @input="trackFieldChange('name_first')"
                      >
                      <span
                        v-if="!form.nameFirst"
                        class="private-access-input-placeholder pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[0.96rem] text-black/34"
                        aria-hidden="true"
                      >
                        {{ nameCopy.firstName }}
                      </span>
                      <button
                        v-if="form.nameFirst"
                        type="button"
                        class="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-black/45 transition hover:bg-black/5 hover:text-black"
                        :aria-label="nameCopy.clearFirstName"
                        @click="form.nameFirst = ''"
                      >
                        <X class="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label class="mb-2 block whitespace-nowrap text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-last-name">
                      {{ nameCopy.lastName }} <span class="normal-case tracking-normal text-black/45">· {{ nameCopy.optional }}</span>
                    </label>
                    <div class="relative">
                      <input
                        id="private-access-last-name"
                        v-model="form.nameLast"
                        type="text"
                        autocomplete="family-name"
                        maxlength="80"
                        class="w-full rounded-full border border-black bg-transparent px-5 py-3 pr-11 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        placeholder=" "
                        @focus="trackFieldFocus('name_last')"
                        @input="trackFieldChange('name_last')"
                      >
                      <span
                        v-if="!form.nameLast"
                        class="private-access-input-placeholder pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[0.96rem] text-black/34"
                        aria-hidden="true"
                      >
                        {{ nameCopy.lastName }}
                      </span>
                      <button
                        v-if="form.nameLast"
                        type="button"
                        class="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-black/45 transition hover:bg-black/5 hover:text-black"
                        :aria-label="nameCopy.clearLastName"
                        @click="form.nameLast = ''"
                      >
                        <X class="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-email">
                    {{ copy.emailLabel }}
                  </label>
                  <div class="relative">
                    <input
                      id="private-access-email"
                      ref="emailInput"
                      v-model="form.email"
                      type="email"
                      required
                      autocomplete="email"
                      class="w-full rounded-full border border-black bg-transparent px-5 py-3 pr-11 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                      placeholder=" "
                      @focus="trackFieldFocus('email')"
                      @input="trackFieldChange('email')"
                    >
                    <span
                      v-if="!form.email"
                      class="private-access-input-placeholder pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[0.96rem] text-black/34"
                      aria-hidden="true"
                    >
                      {{ copy.emailPlaceholder }}
                    </span>
                    <button
                      v-if="form.email"
                      type="button"
                      class="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-black/45 transition hover:bg-black/5 hover:text-black"
                      :aria-label="copy.clearEmail"
                      @click="form.email = ''"
                    >
                      <X class="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                    {{ copy.emailNote }}
                  </p>
                </div>

                <Transition name="pa-field">
                  <div v-if="showMoreFields" style="transition-delay: 0ms">
                    <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-instagram">
                      {{ copy.instagramLabel }}
                    </label>
                    <input
                      id="private-access-instagram"
                      v-model="form.instagram"
                      type="text"
                      autocomplete="off"
                      class="w-full rounded-full border border-black bg-transparent px-5 py-3 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                      :placeholder="copy.instagramPlaceholder"
                      @focus="trackFieldFocus('instagram')"
                      @input="trackFieldChange('instagram')"
                    >
                    <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                      {{ copy.instagramNote }}
                    </p>
                  </div>
                </Transition>

                <Transition name="pa-field">
                  <div v-if="showMoreFields" class="grid gap-4 sm:grid-cols-3" style="transition-delay: 70ms">
                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-product">
                        {{ productInterestLabel }}
                      </label>
                      <select
                        id="private-access-product"
                        v-model="form.product"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('product')"
                        @change="trackFieldChange('product')"
                      >
                        <option v-for="option in productInterestOptions" :key="option.value" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ productInterestNote }}
                      </p>
                    </div>

                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-size">
                        {{ copy.sizeLabel }}
                      </label>
                      <select
                        id="private-access-size"
                        v-model="form.size"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('size')"
                        @change="trackFieldChange('size')"
                      >
                        <option value="">{{ copy.sizePlaceholder }}</option>
                        <option v-for="size in sizes" :key="size" :value="size">{{ size }}</option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ copy.sizeNote }}
                      </p>
                    </div>

                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-gender">
                        {{ captureCopy.genderLabel }}
                      </label>
                      <select
                        id="private-access-gender"
                        v-model="form.gender"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('gender')"
                        @change="trackFieldChange('gender')"
                      >
                        <option v-for="option in captureCopy.genderOptions" :key="option.value || 'gender-placeholder'" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ captureCopy.genderNote }}
                      </p>
                    </div>
                  </div>
                </Transition>

                <Transition name="pa-field">
                  <div v-if="showMoreFields" class="grid gap-4 sm:grid-cols-2" style="transition-delay: 140ms">
                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-country">
                        {{ captureCopy.countryLabel }}
                      </label>
                      <select
                        id="private-access-country"
                        v-model="form.country"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('country')"
                        @change="trackFieldChange('country')"
                      >
                        <option v-for="option in captureCopy.countryOptions" :key="option.value || 'country-placeholder'" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ captureCopy.countryNote }}
                      </p>
                    </div>

                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-travel-window">
                        {{ captureCopy.travelWindowLabel }}
                      </label>
                      <select
                        id="private-access-travel-window"
                        v-model="form.travelWindow"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('travel_window')"
                        @change="trackFieldChange('travel_window')"
                      >
                        <option v-for="option in captureCopy.travelWindowOptions" :key="option.value || 'travel-placeholder'" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ captureCopy.travelWindowNote }}
                      </p>
                    </div>
                  </div>
                </Transition>

                <Transition name="pa-field">
                  <div v-if="showMoreFields" class="grid gap-4 sm:grid-cols-2" style="transition-delay: 210ms">
                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-wear-context">
                        {{ captureCopy.wearContextLabel }}
                      </label>
                      <select
                        id="private-access-wear-context"
                        v-model="form.wearContext"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('wear_context')"
                        @change="trackFieldChange('wear_context')"
                      >
                        <option v-for="option in captureCopy.wearContextOptions" :key="option.value || 'wear-placeholder'" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ captureCopy.wearContextNote }}
                      </p>
                    </div>

                    <div>
                      <label class="mb-2 block text-[0.66rem] uppercase tracking-[0.26em] text-black/68" for="private-access-purchase-intent">
                        {{ captureCopy.purchaseIntentLabel }}
                      </label>
                      <select
                        id="private-access-purchase-intent"
                        v-model="form.purchaseIntent"
                        class="private-access-select w-full appearance-none rounded-full border border-black bg-transparent py-3 pl-5 pr-12 text-[0.96rem] text-black outline-none transition focus:ring-2 focus:ring-inset focus:ring-black"
                        @focus="trackFieldFocus('purchase_intent')"
                        @change="trackFieldChange('purchase_intent')"
                      >
                        <option v-for="option in captureCopy.purchaseIntentOptions" :key="option.value || 'intent-placeholder'" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <p class="mt-2 text-[0.8rem] leading-[1.6] text-black/72">
                        {{ captureCopy.purchaseIntentNote }}
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>

              <div class="sr-only" aria-hidden="true">
                <input v-model="form.website" type="text" autocomplete="off" tabindex="-1">
                <input v-model="form.company" type="text" autocomplete="off" tabindex="-1">
              </div>

              <Transition name="pa-field">
                <div v-if="showMoreFields || compact" style="transition-delay: 280ms">
                  <label v-if="!compact" class="mt-6 flex items-start gap-4">
                    <input
                      v-model="form.optIn"
                      type="checkbox"
                      class="mt-0.5 h-6 w-6 shrink-0 cursor-pointer rounded-[0.35rem] border border-black bg-white/85 accent-black shadow-[0_8px_20px_-16px_rgba(0,0,0,0.7)] transition focus:ring-2 focus:ring-black/12 focus:ring-offset-2"
                      @focus="trackFieldFocus('opt_in')"
                      @change="trackFieldChange('opt_in')"
                    >
                    <span class="text-[0.96rem] leading-[1.72] text-black/82">
                      {{ copy.consent }}
                    </span>
                  </label>

                  <p v-if="!compact" class="mt-4 max-w-[28rem] text-[0.82rem] leading-[1.72] text-black/72">
                    {{ copy.privacyPrefix }}
                    <NuxtLink :to="privacyPath" class="border-b border-black/25 text-black transition hover:border-black/60">
                      {{ copy.privacyLink }}
                    </NuxtLink>
                    {{ copy.privacySuffix }}
                  </p>

                  <p v-if="errorMessage" class="mt-4 text-[0.88rem] leading-[1.7] text-[#a23418]" aria-live="polite">
                    {{ errorMessage }}
                  </p>

                  <p v-if="!compact" class="mt-6 text-[0.66rem] uppercase tracking-[0.28em] text-black/68">
                    {{ copy.submitNote }}
                  </p>

                  <button
                    ref="submitButton"
                    type="submit"
                    :class="submitButtonClass"
                    :disabled="isSubmitting"
                  >
                    {{ isSubmitting ? copy.submitting : copy.submit }}
                  </button>
                </div>
              </Transition>

              <PrivateAccessSocialFallback
                v-if="showSocialFallback"
                variant="fallback"
                form-location="home_private_access"
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { X } from 'lucide-vue-next'
import { getPrivateAccessNameCopy } from '~/utils/privateAccessNameCopy'
import { useHomeLuxuryContext } from '~/composables/useHomeLuxuryContext'
import { getLocaleIso, type SiteLocaleCode } from '~/utils/locales'
import { getElementViewportMetrics, getTimingMetrics } from '~/utils/signalskitViewport'
import type { HomePrivateAccessProduct } from '~/utils/homePrivateAccessForm'
import {
  buildPrivateAccessTrackingData,
  getHomePrivateAccessErrorMessage,
  submitHomePrivateAccessRequest
} from '~/utils/homePrivateAccessForm'

const props = withDefaults(defineProps<{
  autofocusEmail?: boolean
  compact?: boolean
  embedded?: boolean
  waitingListCount?: number | null
}>(), {
  autofocusEmail: false,
  compact: false,
  embedded: false,
  waitingListCount: null
})

const { currentLocale, localePrefix } = useHomeLuxuryContext()
const nameCopy = computed(() => getPrivateAccessNameCopy(currentLocale.value))
const { trackPrivateAccessRequest } = useGA()
const route = useRoute()
const signalsKit = import.meta.client ? useSignalsKit() : null

const form = reactive({
  company: '',
  country: '',
  email: '',
  gender: '',
  instagram: '',
  intentContext: '',
  nameFirst: '',
  nameLast: '',
  optIn: false,
  product: 'ankle-boots' as HomePrivateAccessProduct,
  purchaseIntent: '',
  size: '',
  sourceCategory: '',
  sourcePagePath: '',
  travelWindow: '',
  wearContext: '',
  website: ''
})

const isSubmitting = ref(false)
const isSuccess = ref(false)
const errorMessage = ref('')
const sectionRoot = ref<HTMLElement | null>(null)
const formRoot = ref<HTMLFormElement | null>(null)
const submitButton = ref<HTMLButtonElement | null>(null)
const firstNameInput = ref<HTMLInputElement | null>(null)
const emailInput = ref<HTMLInputElement | null>(null)
const sizes = ['35', '36', '37', '38', '39', '40', '41']

const mountedAt = import.meta.client ? Date.now() : null
let formViewObserver: IntersectionObserver | null = null
let hasTrackedArrival = false
let hasTrackedFormView = false
let hasTrackedFormStart = false
let hasTrackedFormAbandonment = false
let isProgrammaticFocus = false
let socialFallbackTimer: ReturnType<typeof setTimeout> | null = null
const SOCIAL_FALLBACK_DELAY_MS = 20_000
const showSocialFallback = ref(false)
const focusedFields = new Set<string>()
const touchedFields = new Set<string>()

type PrivateAccessCopy = {
  body: string
  clearEmail: string
  consent: string
  eyebrow: string
  emailLabel: string
  emailNote: string
  emailPlaceholder: string
  formBody: string
  formEyebrow: string
  formTitle: string
  instagramLabel: string
  instagramNote: string
  instagramPlaceholder: string
  privacyLink: string
  privacyPrefix: string
  privacySuffix: string
  sizeLabel: string
  sizeNote: string
  sizePlaceholder: string
  submit: string
  submitNote: string
  submitting: string
  successBody: string
  successEyebrow: string
  successTitle: string
  title: string
  waitingListAria: string
  waitingListLabel: string
  waitingListText: string
}

const PRIVATE_ACCESS_COPY = {
  en: {
    body: 'Production is limited by series.',
    clearEmail: 'Clear email',
    consent: 'I also agree to receive production opening updates from Kendrick Paris.',
    eyebrow: 'Production waitlist',
    emailLabel: 'Email',
    emailNote: 'Required to confirm your place in line.',
    emailPlaceholder: 'name@example.com',
    formBody: 'Access follows production capacity.',
    formEyebrow: 'Limited production',
    formTitle: 'Access follows production capacity.',
    instagramLabel: 'Instagram',
    instagramNote: 'Optional. Helps us avoid duplicate requests.',
    instagramPlaceholder: '@handle',
    privacyLink: 'Privacy Policy',
    privacyPrefix: 'We use these details to manage the request and production waitlist. Marketing emails are sent only if the box is checked. See the ',
    privacySuffix: '.',
    sizeLabel: 'Size',
    sizeNote: 'For sizing reference only.',
    sizePlaceholder: 'Choose a size',
    submit: 'Send request',
    submitNote: 'Access follows the order of registration.',
    submitting: 'Submitting...',
    successBody: 'Access follows the order of registration, within the pairs available in each series. We will write to you as soon as a place opens.',
    successEyebrow: 'Registered',
    successTitle: 'Request sent.',
    title: 'Join the next production opening.',
    waitingListAria: 'Private access count',
    waitingListLabel: 'Waitlist',
    waitingListText: 'Requests are handled in registration order for each production opening.'
  },
  fr: {
    body: 'Production limitée par série.',
    clearEmail: 'Effacer l’email',
    consent: 'J’accepte aussi de recevoir les ouvertures de production de Kendrick Paris.',
    eyebrow: 'Liste d’attente production',
    emailLabel: 'Email',
    emailNote: 'Nécessaire pour confirmer votre place dans l’ordre d’inscription.',
    emailPlaceholder: 'nom@exemple.com',
    formBody: 'L’accès suit la capacité de production.',
    formEyebrow: 'Production limitée',
    formTitle: 'Accès selon capacité de production.',
    instagramLabel: 'Instagram',
    instagramNote: 'Optionnel. Ajoutez votre compte si vous souhaitez donner plus de contexte à votre demande.',
    instagramPlaceholder: '@identifiant',
    privacyLink: 'politique de confidentialité',
    privacyPrefix: 'Nous utilisons ces informations pour gérer la demande et la liste d’attente de production. Les emails marketing sont envoyés uniquement si la case est cochée. Voir la ',
    privacySuffix: '.',
    sizeLabel: 'Pointure',
    sizeNote: 'À titre indicatif uniquement.',
    sizePlaceholder: 'Choisir une pointure',
    submit: 'Envoyer la demande',
    submitNote: 'L’accès suit l’ordre d’inscription.',
    submitting: 'Envoi...',
    successBody: 'L’accès suit l’ordre d’inscription, dans la limite des paires de chaque série. Nous vous écrivons dès qu’une place est disponible.',
    successEyebrow: 'Inscription reçue',
    successTitle: 'Demande envoyée.',
    title: 'Rejoindre la prochaine ouverture de production.',
    waitingListAria: 'Nombre de demandes d’accès privé',
    waitingListLabel: 'Liste d’attente',
    waitingListText: 'Les demandes sont traitées par ordre d’inscription pour chaque ouverture de production.'
  },
  es: {
    body: 'La producción es limitada por serie.',
    clearEmail: 'Borrar email',
    consent: 'También acepto recibir aperturas de producción de Kendrick Paris.',
    eyebrow: 'Lista de espera de producción',
    emailLabel: 'Email',
    emailNote: 'Necesario para confirmar tu lugar en el orden de inscripción.',
    emailPlaceholder: 'nombre@ejemplo.com',
    formBody: 'El acceso depende de la capacidad de producción.',
    formEyebrow: 'Producción limitada',
    formTitle: 'El acceso depende de la capacidad de producción.',
    instagramLabel: 'Instagram',
    instagramNote: 'Opcional. Ayuda a evitar solicitudes duplicadas.',
    instagramPlaceholder: '@usuario',
    privacyLink: 'Política de privacidad',
    privacyPrefix: 'Usamos estos datos para gestionar la solicitud y la lista de espera de producción. Los emails de marketing solo se envían si marcas la casilla. Consulta la ',
    privacySuffix: '.',
    sizeLabel: 'Talla',
    sizeNote: 'Solo como referencia de talla.',
    sizePlaceholder: 'Elegir una talla',
    submit: 'Enviar solicitud',
    submitNote: 'El acceso sigue el orden de inscripción.',
    submitting: 'Enviando...',
    successBody: 'El acceso sigue el orden de inscripción, dentro del límite de pares de cada serie. Te escribiremos en cuanto haya una plaza disponible.',
    successEyebrow: 'Registrada',
    successTitle: 'Solicitud enviada.',
    title: 'Únete a la próxima apertura de producción.',
    waitingListAria: 'Número de solicitudes de acceso privado',
    waitingListLabel: 'Lista de espera',
    waitingListText: 'Las solicitudes se tratan por orden de inscripción en cada apertura de producción.'
  },
  pt: {
    body: 'A produção é limitada por série.',
    clearEmail: 'Limpar email',
    consent: 'Aceito também receber aberturas de produção da Kendrick Paris.',
    eyebrow: 'Lista de espera de produção',
    emailLabel: 'Email',
    emailNote: 'Necessário para confirmar o seu lugar na ordem de inscrição.',
    emailPlaceholder: 'nome@exemplo.com',
    formBody: 'O acesso acompanha a capacidade de produção.',
    formEyebrow: 'Produção limitada',
    formTitle: 'O acesso acompanha a capacidade de produção.',
    instagramLabel: 'Instagram',
    instagramNote: 'Opcional. Ajuda-nos a evitar pedidos duplicados.',
    instagramPlaceholder: '@utilizador',
    privacyLink: 'Política de privacidade',
    privacyPrefix: 'Usamos estes dados para gerir o pedido e a lista de espera de produção. Emails de marketing só são enviados se a caixa estiver selecionada. Consulte a ',
    privacySuffix: '.',
    sizeLabel: 'Tamanho',
    sizeNote: 'Apenas como referência de tamanho.',
    sizePlaceholder: 'Escolher um tamanho',
    submit: 'Enviar pedido',
    submitNote: 'O acesso segue a ordem de inscrição.',
    submitting: 'A enviar...',
    successBody: 'O acesso segue a ordem de inscrição, dentro do limite de pares de cada série. Escrevemos-lhe assim que houver uma vaga disponível.',
    successEyebrow: 'Registado',
    successTitle: 'Pedido enviado.',
    title: 'Entre na próxima abertura de produção.',
    waitingListAria: 'Número de pedidos de acesso privado',
    waitingListLabel: 'Lista de espera',
    waitingListText: 'Os pedidos são tratados por ordem de inscrição em cada abertura de produção.'
  },
  ja: {
    body: '生産はシリーズごとに限られています。',
    clearEmail: 'メールを消去',
    consent: 'Kendrick Parisのプライベートオープン案内を受け取ることにも同意します。',
    eyebrow: '生産ウェイトリスト',
    emailLabel: 'メール',
    emailNote: '登録順の確認に必要です。',
    emailPlaceholder: 'name@example.com',
    formBody: 'アクセスは生産枠に応じてご案内します。',
    formEyebrow: '限定生産',
    formTitle: 'アクセスは生産枠に応じてご案内します。',
    instagramLabel: 'Instagram',
    instagramNote: '任意。重複リクエストの確認に役立ちます。',
    instagramPlaceholder: '@handle',
    privacyLink: 'プライバシーポリシー',
    privacyPrefix: 'これらの情報はリクエストと生産ウェイトリストの管理に使用します。マーケティングメールはチェックを入れた場合のみ送信されます。詳しくは',
    privacySuffix: 'をご覧ください。',
    sizeLabel: 'サイズ',
    sizeNote: 'サイズ確認の参考としてのみ使用します。',
    sizePlaceholder: 'サイズを選択',
    submit: 'リクエストを送信',
    submitNote: 'アクセスは登録順です。',
    submitting: '送信中...',
    successBody: 'アクセスは登録順で、各シリーズの足数の範囲内となります。空きが出次第ご連絡します。',
    successEyebrow: '登録済み',
    successTitle: 'リクエストを送信しました。',
    title: '次の生産オープンに登録。',
    waitingListAria: 'プライベートアクセス申請数',
    waitingListLabel: 'ウェイトリスト',
    waitingListText: 'リクエストは各生産オープンごとに登録順で扱われます。'
  },
  ko: {
    body: '생산은 시리즈별로 제한됩니다.',
    clearEmail: '이메일 지우기',
    consent: 'Kendrick Paris의 프라이빗 오픈 안내를 받는 것에도 동의합니다.',
    eyebrow: '생산 대기 명단',
    emailLabel: '이메일',
    emailNote: '등록 순서를 확인하기 위해 필요합니다.',
    emailPlaceholder: 'name@example.com',
    formBody: '생산 가능 수량에 따라 접근이 열립니다.',
    formEyebrow: '한정 생산',
    formTitle: '생산 가능 수량에 따라 접근이 열립니다.',
    instagramLabel: 'Instagram',
    instagramNote: '선택 사항입니다. 중복 요청을 피하는 데 도움이 됩니다.',
    instagramPlaceholder: '@handle',
    privacyLink: '개인정보 처리방침',
    privacyPrefix: '이 정보는 요청과 생산 대기 명단을 관리하는 데 사용됩니다. 마케팅 이메일은 체크한 경우에만 발송됩니다. ',
    privacySuffix: '을 확인하세요.',
    sizeLabel: '사이즈',
    sizeNote: '사이즈 참고용입니다.',
    sizePlaceholder: '사이즈 선택',
    submit: '요청 보내기',
    submitNote: '접근은 등록 순서대로 제공됩니다.',
    submitting: '전송 중...',
    successBody: '접근은 등록 순서에 따라, 각 시리즈의 수량 한도 내에서 제공됩니다. 자리가 나는 대로 연락드리겠습니다.',
    successEyebrow: '등록 완료',
    successTitle: '요청이 전송되었습니다.',
    title: '다음 생산 오픈에 등록하세요.',
    waitingListAria: '프라이빗 액세스 요청 수',
    waitingListLabel: '대기 명단',
    waitingListText: '요청은 각 생산 오픈마다 등록 순서대로 처리됩니다.'
  },
  de: {
    body: 'Die Produktion ist je Serie begrenzt.',
    clearEmail: 'E-Mail löschen',
    consent: 'Ich möchte auch private Öffnungen von Kendrick Paris erhalten.',
    eyebrow: 'Produktionswarteliste',
    emailLabel: 'Email',
    emailNote: 'Erforderlich, um Ihren Platz in der Anmeldereihenfolge zu bestätigen.',
    emailPlaceholder: 'name@beispiel.com',
    formBody: 'Der Zugang richtet sich nach der Produktionskapazität.',
    formEyebrow: 'Begrenzte Produktion',
    formTitle: 'Der Zugang richtet sich nach der Produktionskapazität.',
    instagramLabel: 'Instagram',
    instagramNote: 'Optional. Hilft uns, doppelte Anfragen zu vermeiden.',
    instagramPlaceholder: '@profil',
    privacyLink: 'Datenschutzerklärung',
    privacyPrefix: 'Wir verwenden diese Angaben, um die Anfrage und die Produktionswarteliste zu verwalten. Marketing-E-Mails werden nur gesendet, wenn das Feld aktiviert ist. Siehe ',
    privacySuffix: '.',
    sizeLabel: 'Größe',
    sizeNote: 'Nur als Größenreferenz.',
    sizePlaceholder: 'Größe wählen',
    submit: 'Anfrage senden',
    submitNote: 'Der Zugang folgt der Reihenfolge der Anmeldung.',
    submitting: 'Wird gesendet...',
    successBody: 'Der Zugang folgt der Reihenfolge der Anmeldung, im Rahmen der Paare jeder Serie. Wir schreiben Ihnen, sobald ein Platz frei wird.',
    successEyebrow: 'Angemeldet',
    successTitle: 'Anfrage gesendet.',
    title: 'Für die nächste Produktionsöffnung anmelden.',
    waitingListAria: 'Anzahl der privaten Zugangsanfragen',
    waitingListLabel: 'Warteliste',
    waitingListText: 'Anfragen werden bei jeder Produktionsöffnung nach Anmeldereihenfolge bearbeitet.'
  },
  it: {
    body: 'La produzione è limitata per serie.',
    clearEmail: 'Cancella email',
    consent: 'Accetto anche di ricevere aperture private da Kendrick Paris.',
    eyebrow: 'Lista d’attesa produzione',
    emailLabel: 'Email',
    emailNote: 'Necessaria per confermare il tuo posto nell’ordine di iscrizione.',
    emailPlaceholder: 'nome@esempio.com',
    formBody: 'L’accesso segue la capacità produttiva.',
    formEyebrow: 'Produzione limitata',
    formTitle: 'L’accesso segue la capacità produttiva.',
    instagramLabel: 'Instagram',
    instagramNote: 'Facoltativo. Aiuta a evitare richieste duplicate.',
    instagramPlaceholder: '@profilo',
    privacyLink: 'Informativa sulla privacy',
    privacyPrefix: 'Usiamo questi dati per gestire la richiesta e la lista d’attesa di produzione. Le email marketing vengono inviate solo se la casella è selezionata. Vedi l’',
    privacySuffix: '.',
    sizeLabel: 'Taglia',
    sizeNote: 'Solo come riferimento taglia.',
    sizePlaceholder: 'Scegli una taglia',
    submit: 'Inviare la richiesta',
    submitNote: 'L’accesso segue l’ordine di iscrizione.',
    submitting: 'Invio...',
    successBody: 'L’accesso segue l’ordine di iscrizione, nei limiti dei paia di ogni serie. Ti scriveremo non appena si libera un posto.',
    successEyebrow: 'Registrata',
    successTitle: 'Richiesta inviata.',
    title: 'Entra nella prossima apertura di produzione.',
    waitingListAria: 'Numero di richieste di accesso privato',
    waitingListLabel: 'Lista d’attesa',
    waitingListText: 'Le richieste vengono gestite in ordine di iscrizione per ogni apertura di produzione.'
  },
  zh: {
    body: '每个系列均限量生产。',
    clearEmail: '清除邮箱',
    consent: '我也同意接收 Kendrick Paris 的私人开放通知。',
    eyebrow: '生产候补名单',
    emailLabel: '邮箱',
    emailNote: '用于确认您的登记顺序。',
    emailPlaceholder: 'name@example.com',
    formBody: '开放名额取决于生产容量。',
    formEyebrow: '限量生产',
    formTitle: '开放名额取决于生产容量。',
    instagramLabel: 'Instagram',
    instagramNote: '可选。帮助我们避免重复申请。',
    instagramPlaceholder: '@handle',
    privacyLink: '隐私政策',
    privacyPrefix: '我们使用这些信息来管理申请和生产候补名单。只有勾选后才会发送营销邮件。请查看',
    privacySuffix: '。',
    sizeLabel: '尺码',
    sizeNote: '仅作为尺码参考。',
    sizePlaceholder: '选择尺码',
    submit: '提交申请',
    submitNote: '按登记顺序开放。',
    submitting: '发送中...',
    successBody: '按登记顺序开放，并以每个系列的鞋款数量为限。有名额时我们会立即与您联系。',
    successEyebrow: '已登记',
    successTitle: '申请已发送。',
    title: '加入下一次生产开放。',
    waitingListAria: '私人访问申请数量',
    waitingListLabel: '候补名单',
    waitingListText: '每次生产开放均按登记顺序处理申请。'
  }
} satisfies Record<SiteLocaleCode, PrivateAccessCopy>

const copy = computed(() => {
  return PRIVATE_ACCESS_COPY[currentLocale.value] || PRIVATE_ACCESS_COPY.en
})

const compact = computed(() => props.compact)
const showMoreFields = computed(() => !props.compact && form.nameFirst.trim().length > 0 && form.email.trim().length > 0)

const hasWaitingListCount = computed(() => typeof props.waitingListCount === 'number' && props.waitingListCount > 0)
const privacyPath = computed(() => `${localePrefix.value}/privacy`)

const sectionClass = computed(() => [
  'scroll-mt-28 text-[#f1ebdf] sm:scroll-mt-32 lg:scroll-mt-36',
  props.embedded ? 'bg-transparent' : 'bg-[#090807]'
])

const outerClass = computed(() => props.compact
  ? 'mx-auto w-full max-w-[1700px] min-w-0 px-6 py-6 sm:px-8 sm:py-8 lg:px-12 lg:py-10'
  : 'mx-auto w-full max-w-[1700px] min-w-0 px-6 py-10 sm:px-8 sm:py-14 lg:px-12 lg:py-20')

const introPanelClass = computed(() => props.compact
  ? 'bg-[#0e0d0b] px-6 py-6 sm:px-8 sm:py-7 lg:px-10 lg:py-8'
  : 'bg-[#0e0d0b] px-6 py-6 sm:px-8 sm:py-10 lg:px-10 lg:py-12')

const introTitleClass = computed(() => props.compact
  ? 'mt-4 max-w-[12ch] font-[\'Playfair_Display\'] text-[2.25rem] leading-[0.94] text-[#f5efe4] sm:text-[3rem] sm:leading-[0.9]'
  : 'mt-4 max-w-[12ch] font-[\'Playfair_Display\'] text-[2.35rem] leading-[0.94] text-[#f5efe4] sm:mt-5 sm:text-[3.7rem] sm:leading-[0.9]')

const introBodyClass = computed(() => props.compact
  ? 'mt-3 max-w-[20rem] text-[0.92rem] leading-[1.5] text-[#f1ebdf]/80'
  : 'mt-4 max-w-[20rem] text-[0.95rem] leading-[1.62] text-[#f1ebdf]/80 sm:mt-5 sm:text-[1rem]')

const formPanelClass = computed(() => props.compact
  ? 'bg-[#f5efe4] px-6 py-6 text-black sm:px-8 sm:py-7 lg:px-10 lg:py-8'
  : 'bg-[#f5efe4] px-6 py-6 text-black sm:px-8 sm:py-10 lg:px-10 lg:py-12')

const formTitleClass = computed(() => props.compact
  ? 'mt-3 max-w-[13ch] font-[\'Playfair_Display\'] text-[1.9rem] leading-[0.96] text-black sm:text-[2.45rem] sm:leading-[0.92]'
  : 'mt-4 max-w-[12ch] font-[\'Playfair_Display\'] text-[2rem] leading-[0.96] text-black sm:mt-5 sm:text-[2.9rem] sm:leading-[0.92]')

const formBodyClass = computed(() => props.compact
  ? 'mt-3 max-w-[18rem] text-[0.94rem] leading-[1.5] text-black/80'
  : 'mt-4 max-w-[18rem] text-[0.96rem] leading-[1.62] text-black/80 sm:mt-5 sm:text-[0.98rem]')

const fieldsClass = computed(() => props.compact
  ? 'mt-5 space-y-4'
  : 'mt-6 space-y-4 sm:mt-8')

const submitButtonClass = computed(() => props.compact
  ? 'mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-black px-6 py-3 text-center text-[0.68rem] uppercase tracking-[0.24em] text-white transition hover:bg-black/88 disabled:cursor-not-allowed disabled:opacity-60'
  : 'mt-7 inline-flex min-h-12 items-center justify-center rounded-full bg-black px-6 py-3 text-center text-[0.72rem] uppercase tracking-[0.26em] text-white transition hover:bg-black/88 disabled:cursor-not-allowed disabled:opacity-60')

const productInterestLabel = computed(() => currentLocale.value === 'fr' ? 'Silhouette' : 'Silhouette')
const productInterestNote = computed(() => currentLocale.value === 'fr'
  ? 'Optionnel. Permet de relier la demande à la prochaine ouverture adaptée.'
  : 'Optional. Helps connect the request to the right next opening.'
)
const productInterestOptions = computed<Array<{ value: HomePrivateAccessProduct, label: string }>>(() => {
  if (currentLocale.value === 'fr') {
    return [
      { value: 'ankle-boots', label: 'Bottines' },
      { value: 'boots', label: 'Bottes' },
      { value: 'pumps', label: 'Escarpins' },
      { value: 'sandals', label: 'Sandales' }
    ]
  }

  return [
    { value: 'ankle-boots', label: 'Ankle boots' },
    { value: 'boots', label: 'Boots' },
    { value: 'pumps', label: 'Pumps' },
    { value: 'sandals', label: 'Sandals' }
  ]
})

type PrivateAccessSelectOption = { value: string, label: string }

type PrivateAccessCaptureCopy = {
  countryLabel: string
  countryNote: string
  countryOptions: PrivateAccessSelectOption[]
  genderLabel: string
  genderNote: string
  genderOptions: PrivateAccessSelectOption[]
  purchaseIntentLabel: string
  purchaseIntentNote: string
  purchaseIntentOptions: PrivateAccessSelectOption[]
  travelWindowLabel: string
  travelWindowNote: string
  travelWindowOptions: PrivateAccessSelectOption[]
  wearContextLabel: string
  wearContextNote: string
  wearContextOptions: PrivateAccessSelectOption[]
}

const PRIVATE_ACCESS_CAPTURE_COPY: Record<'en' | 'fr', PrivateAccessCaptureCopy> = {
  en: {
    countryLabel: 'Country',
    countryNote: 'Optional. Helps us understand where requests come from.',
    countryOptions: [
      { value: '', label: 'Select a country' },
      { value: 'us', label: 'United States' },
      { value: 'fr', label: 'France' },
      { value: 'gb', label: 'United Kingdom' },
      { value: 'ca', label: 'Canada' },
      { value: 'au', label: 'Australia' },
      { value: 'de', label: 'Germany' },
      { value: 'it', label: 'Italy' },
      { value: 'es', label: 'Spain' },
      { value: 'pt', label: 'Portugal' },
      { value: 'jp', label: 'Japan' },
      { value: 'kr', label: 'Korea' },
      { value: 'cn', label: 'China' },
      { value: 'other', label: 'Other' }
    ],
    genderLabel: 'Gender',
    genderNote: 'Optional. Helps us address you correctly.',
    genderOptions: [
      { value: '', label: 'Not specified' },
      { value: 'female', label: 'Woman' },
      { value: 'male', label: 'Man' },
      { value: 'other', label: 'Other' }
    ],
    purchaseIntentLabel: 'Interest',
    purchaseIntentNote: 'Optional. Useful for sizing future production openings.',
    purchaseIntentOptions: [
      { value: '', label: 'Select interest' },
      { value: 'private_list', label: 'Keep me on the waitlist' },
      { value: 'preorder_interest', label: 'Interested in preorder' },
      { value: 'reserve_size', label: 'Reserve if my size opens' },
      { value: 'exploring', label: 'Just exploring' }
    ],
    travelWindowLabel: 'Paris timing',
    travelWindowNote: 'Optional. Especially useful for Paris trips.',
    travelWindowOptions: [
      { value: '', label: 'Select timing' },
      { value: 'already_in_paris', label: 'Already in Paris' },
      { value: 'within_1_month', label: 'Within 1 month' },
      { value: '1_3_months', label: '1 to 3 months' },
      { value: '3_6_months', label: '3 to 6 months' },
      { value: 'later', label: 'Later' },
      { value: 'no_trip_planned', label: 'No trip planned' }
    ],
    wearContextLabel: 'Evening use',
    wearContextNote: 'Optional. Helps us understand the intended use.',
    wearContextOptions: [
      { value: '', label: 'Select use' },
      { value: 'paris_after_dark', label: 'Paris after dark' },
      { value: 'dinner_to_club', label: 'Dinner to club' },
      { value: 'hotel_bar', label: 'Hotel bar' },
      { value: 'strict_club', label: 'Strict club' },
      { value: 'private_event', label: 'Private event' },
      { value: 'wedding', label: 'Wedding or ceremony' },
      { value: 'still_deciding', label: 'Still deciding' }
    ]
  },
  fr: {
    countryLabel: 'Pays',
    countryNote: 'Optionnel. Permet de comprendre d’où viennent les demandes.',
    countryOptions: [
      { value: '', label: 'Choisir un pays' },
      { value: 'us', label: 'États-Unis' },
      { value: 'fr', label: 'France' },
      { value: 'gb', label: 'Royaume-Uni' },
      { value: 'ca', label: 'Canada' },
      { value: 'au', label: 'Australie' },
      { value: 'de', label: 'Allemagne' },
      { value: 'it', label: 'Italie' },
      { value: 'es', label: 'Espagne' },
      { value: 'pt', label: 'Portugal' },
      { value: 'jp', label: 'Japon' },
      { value: 'kr', label: 'Corée' },
      { value: 'cn', label: 'Chine' },
      { value: 'other', label: 'Autre' }
    ],
    genderLabel: 'Genre',
    genderNote: 'Optionnel. Nous permet de nous adresser correctement à vous.',
    genderOptions: [
      { value: '', label: 'Non précisé' },
      { value: 'female', label: 'Femme' },
      { value: 'male', label: 'Homme' },
      { value: 'other', label: 'Autre' }
    ],
    purchaseIntentLabel: 'Intention',
    purchaseIntentNote: 'Optionnel. Utile pour calibrer les prochaines ouvertures privées.',
    purchaseIntentOptions: [
      { value: '', label: 'Choisir une intention' },
      { value: 'private_list', label: 'Rester sur la liste d’attente' },
      { value: 'preorder_interest', label: 'Intéressée par une précommande' },
      { value: 'reserve_size', label: 'Réserver si ma pointure ouvre' },
      { value: 'exploring', label: 'Je regarde seulement' }
    ],
    travelWindowLabel: 'Timing Paris',
    travelWindowNote: 'Optionnel. Particulièrement utile pour les séjours à Paris.',
    travelWindowOptions: [
      { value: '', label: 'Choisir un timing' },
      { value: 'already_in_paris', label: 'Déjà à Paris' },
      { value: 'within_1_month', label: 'Dans le mois' },
      { value: '1_3_months', label: 'Dans 1 à 3 mois' },
      { value: '3_6_months', label: 'Dans 3 à 6 mois' },
      { value: 'later', label: 'Plus tard' },
      { value: 'no_trip_planned', label: 'Pas de séjour prévu' }
    ],
    wearContextLabel: 'Usage soir',
    wearContextNote: 'Optionnel. Permet de comprendre l’usage prévu.',
    wearContextOptions: [
      { value: '', label: 'Choisir un usage' },
      { value: 'paris_after_dark', label: 'Paris de nuit' },
      { value: 'dinner_to_club', label: 'Dîner puis club' },
      { value: 'hotel_bar', label: 'Bar d’hôtel' },
      { value: 'strict_club', label: 'Club sélectif' },
      { value: 'private_event', label: 'Événement privé' },
      { value: 'wedding', label: 'Mariage ou cérémonie' },
      { value: 'still_deciding', label: 'Je décide encore' }
    ]
  }
}

const captureCopy = computed(() => currentLocale.value === 'fr'
  ? PRIVATE_ACCESS_CAPTURE_COPY.fr
  : PRIVATE_ACCESS_CAPTURE_COPY.en
)

const getQueryValue = (value: unknown) => {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : ''
  return typeof value === 'string' ? value : ''
}

const resolvePrivateAccessSource = () => {
  const sourcePath = getQueryValue(route.query.pa_source)
  const intentContext = getQueryValue(route.query.pa_context) || 'home_private_access'
  const sourceCategory = getQueryValue(route.query.pa_category) ||
    (sourcePath.startsWith('/magazine') || sourcePath.startsWith('/fr/magazine') ? 'magazine' : 'home')

  return {
    intentContext,
    sourceCategory,
    sourcePagePath: sourcePath.startsWith('/') ? sourcePath : route.path
  }
}

type PrivateAccessFormEventName =
  | 'private_access_form_arrival'
  | 'private_access_form_view'
  | 'private_access_form_start'
  | 'private_access_form_focus'
  | 'private_access_form_field_change'
  | 'private_access_form_submit_attempt'
  | 'private_access_form_submit_success'
  | 'private_access_form_submit_error'

type PrivateAccessFormField =
  | 'country'
  | 'email'
  | 'gender'
  | 'name_first'
  | 'name_last'
  | 'instagram'
  | 'opt_in'
  | 'product'
  | 'purchase_intent'
  | 'size'
  | 'travel_window'
  | 'wear_context'

const getSourceArticleSlug = (sourcePagePath: string) =>
  sourcePagePath.split('?')[0]?.split('/').filter(Boolean).pop() || 'home'

const getReferrerContext = () => {
  if (typeof document === 'undefined' || !document.referrer) {
    return {}
  }

  try {
    const referrer = new URL(document.referrer)
    return {
      referrer_host: referrer.host,
      referrer_path: referrer.pathname
    }
  } catch {
    return {
      referrer_host: 'unknown',
      referrer_path: 'unknown'
    }
  }
}

const getCompletionSnapshot = () => {
  const hasEmail = form.email.trim().length > 0
  const hasInstagram = form.instagram.trim().length > 0
  const hasSize = form.size.trim().length > 0
  const hasCountry = form.country.trim().length > 0
  const hasGender = form.gender.trim().length > 0
  const hasTravelWindow = form.travelWindow.trim().length > 0
  const hasWearContext = form.wearContext.trim().length > 0
  const hasPurchaseIntent = form.purchaseIntent.trim().length > 0

  return {
    country: form.country || 'not_selected',
    gender: form.gender || 'not_selected',
    has_email: hasEmail,
    has_instagram: hasInstagram,
    has_country: hasCountry,
    has_gender: hasGender,
    has_purchase_intent: hasPurchaseIntent,
    has_size: hasSize,
    has_travel_window: hasTravelWindow,
    has_wear_context: hasWearContext,
    purchase_intent: form.purchaseIntent || 'not_selected',
    product_type: form.product,
    product_selected: Boolean(form.product),
    travel_window: form.travelWindow || 'not_selected',
    wear_context: form.wearContext || 'not_selected',
    marketing_opt_in: form.optIn,
    fields_completed_count: [
      hasEmail,
      hasInstagram,
      hasSize,
      hasCountry,
      hasGender,
      hasTravelWindow,
      hasWearContext,
      hasPurchaseIntent,
      form.optIn
    ].filter(Boolean).length,
    fields_touched_count: touchedFields.size
  }
}

const fieldPayload = (fieldName: PrivateAccessFormField) => {
  // Names are personal data: only report whether they are filled.
  if (fieldName === 'name_first' || fieldName === 'name_last') {
    return {
      field_has_value: Boolean((fieldName === 'name_first' ? form.nameFirst : form.nameLast).trim())
    }
  }

  if (fieldName === 'product') {
    return {
      field_value: form.product,
      product_type: form.product
    }
  }

  if (fieldName === 'size') {
    return {
      field_has_value: form.size.trim().length > 0,
      size: form.size || 'not_selected'
    }
  }

  if (fieldName === 'gender') {
    return {
      field_has_value: form.gender.trim().length > 0,
      field_value: form.gender || 'not_selected',
      gender: form.gender || 'not_selected'
    }
  }

  if (fieldName === 'opt_in') {
    return {
      field_value: form.optIn ? 'true' : 'false',
      marketing_opt_in: form.optIn
    }
  }

  if (fieldName === 'country') {
    return {
      country: form.country || 'not_selected',
      field_has_value: form.country.trim().length > 0,
      field_value: form.country || 'not_selected'
    }
  }

  if (fieldName === 'travel_window') {
    return {
      field_has_value: form.travelWindow.trim().length > 0,
      field_value: form.travelWindow || 'not_selected',
      travel_window: form.travelWindow || 'not_selected'
    }
  }

  if (fieldName === 'wear_context') {
    return {
      field_has_value: form.wearContext.trim().length > 0,
      field_value: form.wearContext || 'not_selected',
      wear_context: form.wearContext || 'not_selected'
    }
  }

  if (fieldName === 'purchase_intent') {
    return {
      field_has_value: form.purchaseIntent.trim().length > 0,
      field_value: form.purchaseIntent || 'not_selected',
      purchase_intent: form.purchaseIntent || 'not_selected'
    }
  }

  return {
    field_has_value: fieldName === 'email'
      ? form.email.trim().length > 0
      : form.instagram.trim().length > 0
  }
}

const trackFormEvent = (
  eventName: PrivateAccessFormEventName,
  payload: Record<string, any> = {},
  element?: Element | null,
  entry?: IntersectionObserverEntry | null
) => {
  const sourceContext = resolvePrivateAccessSource()

  signalsKit?.trackEditorialSignal(eventName, {
    page_path: route.path,
    route_full_path: route.fullPath,
    locale: currentLocale.value,
    form_location: 'home_private_access',
    source_page_path: sourceContext.sourcePagePath,
    source_article_slug: getSourceArticleSlug(sourceContext.sourcePagePath),
    source_category: sourceContext.sourceCategory,
    source_is_magazine: sourceContext.sourceCategory === 'magazine',
    intent_context: sourceContext.intentContext,
    arrived_with_private_access_source: Boolean(getQueryValue(route.query.pa_source)),
    route_hash: route.hash || '',
    ...getReferrerContext(),
    ...getCompletionSnapshot(),
    ...getElementViewportMetrics(element || formRoot.value || sectionRoot.value, entry),
    ...getTimingMetrics(mountedAt),
    ...payload
  })
}

const trackFormArrival = () => {
  if (hasTrackedArrival) return
  if (route.hash !== '#private-access' && !getQueryValue(route.query.pa_source)) return

  hasTrackedArrival = true
  trackFormEvent('private_access_form_arrival', {
    form_step: 'arrival'
  }, sectionRoot.value)
}

const trackFormView = (entry?: IntersectionObserverEntry | null) => {
  if (hasTrackedFormView) return
  hasTrackedFormView = true
  scheduleSocialFallback()
  trackFormEvent('private_access_form_view', {
    form_step: 'view'
  }, sectionRoot.value, entry)
}

// Offer the Instagram follow when the visitor lingers on the form without
// leaving an email, so they still enter the audience. Inline, never a popin.
const revealSocialFallback = () => {
  if (isSuccess.value || showSocialFallback.value || form.email.trim()) return
  showSocialFallback.value = true
}

const scheduleSocialFallback = () => {
  if (socialFallbackTimer || showSocialFallback.value) return
  socialFallbackTimer = setTimeout(revealSocialFallback, SOCIAL_FALLBACK_DELAY_MS)
}

// Programmatic focus (arriving from a CTA) is not the visitor starting the form.
const focusProgrammatically = (input: HTMLElement | null | undefined, options?: FocusOptions) => {
  if (!input) return
  isProgrammaticFocus = true
  try {
    input.focus(options)
  } finally {
    isProgrammaticFocus = false
  }
}

const trackFieldFocus = (fieldName: PrivateAccessFormField) => {
  if (isProgrammaticFocus) return

  if (!focusedFields.has(fieldName)) {
    focusedFields.add(fieldName)
    trackFormEvent('private_access_form_focus', {
      form_step: 'field_focus',
      field_name: fieldName,
      fields_focused_count: focusedFields.size,
      ...fieldPayload(fieldName)
    }, formRoot.value)
  }

  if (hasTrackedFormStart) return
  hasTrackedFormStart = true
  trackFormEvent('private_access_form_start', {
    form_step: 'start',
    first_field_name: fieldName
  }, formRoot.value)
}

const trackFieldChange = (fieldName: PrivateAccessFormField) => {
  if (touchedFields.has(fieldName)) return
  touchedFields.add(fieldName)

  trackFormEvent('private_access_form_field_change', {
    form_step: 'field_change',
    field_name: fieldName,
    ...fieldPayload(fieldName)
  }, formRoot.value)
}

const trackInvalidField = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLSelectElement | null
  const fieldName = target?.id?.replace('private-access-', '') || target?.name || 'unknown'

  trackFormEvent('private_access_form_submit_error', {
    form_step: 'browser_validation',
    request_result: 'error',
    error_type: 'browser_validation',
    field_name: fieldName
  }, target || formRoot.value)

  const sourceContext = resolvePrivateAccessSource()
  signalsKit?.trackFrictionSignal('kendrick_private_access_validation_blocked', {
    surface: 'private_access_form',
    action: 'submit_private_access_request',
    form_location: 'home_private_access',
    form_step: 'browser_validation',
    request_result: 'blocked',
    reason: 'browser_validation',
    field_name: fieldName,
    source_page_path: sourceContext.sourcePagePath,
    source_category: sourceContext.sourceCategory,
    intent_context: sourceContext.intentContext,
    locale: currentLocale.value,
    ...getCompletionSnapshot(),
    ...getTimingMetrics(mountedAt)
  }, {
    kind: 'form_validation',
    severity: 'warning'
  })
}

const getSubmitErrorContext = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return {
      error_type: 'unknown'
    }
  }

  const maybeError = error as {
    statusCode?: number
    statusMessage?: string
    data?: {
      statusCode?: number
      statusMessage?: string
    }
  }

  return {
    error_type: maybeError.statusCode || maybeError.data?.statusCode ? 'api_error' : 'unknown',
    error_status_code: maybeError.statusCode || maybeError.data?.statusCode || 0,
    error_status_message: maybeError.statusMessage || maybeError.data?.statusMessage || 'unknown'
  }
}

const formattedWaitingListCount = computed(() => {
  if (!hasWaitingListCount.value) {
    return ''
  }

  return new Intl.NumberFormat(getLocaleIso(currentLocale.value)).format(props.waitingListCount as number)
})

/* Alternative luxury headline systems:
1. Production waitlist / Early notices for selected releases and limited production openings.
2. Access future drops / Limited openings released according to production capacity.
3. Reserved for subscribers / The next production openings are disclosed first through the waitlist.
4. Early access to selected releases / Limited production notices and future drops, held to the pace of the house.
5. Production waitlist / Future openings are disclosed first to registered subscribers.
*/

const submit = async () => {
  errorMessage.value = ''
  // First name is required on this form (server checks it too).
  if (!form.nameFirst.trim()) {
    errorMessage.value = nameCopy.value.firstNameRequired
    return
  }
  isSubmitting.value = true
  const locale = currentLocale.value
  const sourceContext = resolvePrivateAccessSource()
  form.intentContext = sourceContext.intentContext
  form.sourceCategory = sourceContext.sourceCategory
  form.sourcePagePath = sourceContext.sourcePagePath
  trackFormEvent('private_access_form_submit_attempt', {
    form_step: 'submit_attempt',
    request_result: 'attempt'
  }, submitButton.value || formRoot.value)

  try {
    await submitHomePrivateAccessRequest($fetch, form, locale)
    const eventId = trackPrivateAccessRequest(buildPrivateAccessTrackingData(form, locale))
    trackFormEvent('private_access_form_submit_success', {
      event_id: eventId,
      form_step: 'submit_success',
      request_result: 'success',
      conversion_stage: 'client_form_confirmed',
      business_outcome: 'private_access_form_accepted_by_api',
      dashboard_label: 'Private access form accepted by API',
      is_database_recorded: false
    }, submitButton.value || formRoot.value)

    isSuccess.value = true
  } catch (error: unknown) {
    errorMessage.value = getHomePrivateAccessErrorMessage(error, locale)
    const submitErrorContext = getSubmitErrorContext(error)
    trackFormEvent('private_access_form_submit_error', {
      form_step: 'submit_error',
      request_result: 'error',
      ...submitErrorContext
    }, submitButton.value || formRoot.value)
    signalsKit?.trackFrictionSignal('kendrick_private_access_request_failed', {
      surface: 'private_access_form',
      action: 'submit_private_access_request',
      form_location: 'home_private_access',
      form_step: 'submit_error',
      request_result: 'error',
      reason: 'private_access_api_error',
      locale,
      source_page_path: sourceContext.sourcePagePath,
      source_category: sourceContext.sourceCategory,
      intent_context: sourceContext.intentContext,
      ...getCompletionSnapshot(),
      ...submitErrorContext,
      ...getTimingMetrics(mountedAt)
    }, {
      kind: 'request_failure',
      severity: 'error'
    })
  } finally {
    isSubmitting.value = false
  }
}

const trackFormAbandonment = (reason: string) => {
  if (hasTrackedFormAbandonment || isSuccess.value || isSubmitting.value) return
  if (!hasTrackedFormStart && touchedFields.size === 0 && !form.email.trim()) return

  hasTrackedFormAbandonment = true
  const sourceContext = resolvePrivateAccessSource()
  signalsKit?.trackFrictionSignal('kendrick_private_access_form_abandoned', {
    surface: 'private_access_form',
    action: 'complete_private_access_request',
    form_location: 'home_private_access',
    form_step: 'abandoned',
    request_result: 'abandoned',
    reason,
    locale: currentLocale.value,
    source_page_path: sourceContext.sourcePagePath,
    source_category: sourceContext.sourceCategory,
    intent_context: sourceContext.intentContext,
    ...getCompletionSnapshot(),
    ...getTimingMetrics(mountedAt)
  }, {
    kind: 'conversion_abandonment',
    severity: 'warning'
  })
}

const handlePageHide = () => {
  trackFormAbandonment('pagehide_after_form_start')
}

const applyInitialIntentContext = () => {
  const sourceContext = resolvePrivateAccessSource()
  const hasMatchingWearContext = captureCopy.value.wearContextOptions.some(option =>
    option.value === sourceContext.intentContext
  )

  if (!form.wearContext && hasMatchingWearContext) {
    form.wearContext = sourceContext.intentContext
  }
}

onMounted(() => {
  void useDetectedCountry().resolveOption(captureCopy.value.countryOptions.map(option => option.value)).then((value) => {
    if (value && !form.country) form.country = value
  })
  applyInitialIntentContext()
  trackFormArrival()
  window.addEventListener('pagehide', handlePageHide)

  if (route.hash === '#private-access' || getQueryValue(route.query.pa_source)) {
    // The section arrives via a lazy component plus its own scroll-into-view
    // animation (see HomeLuxuryPage's queueSettledPrivateAccessScroll), so a
    // single focus() at mount often fires before the field is on screen and
    // gets scrolled past. Retry until something takes focus (ours or the
    // visitor's own click), and never fight the page's own scroll position.
    let attempts = 0
    const tryFocusFirstName = () => {
      attempts += 1
      if (document.activeElement && document.activeElement !== document.body) return
      const input = firstNameInput.value
      if (input) {
        focusProgrammatically(input, { preventScroll: true })
        return
      }
      if (attempts < 60) window.setTimeout(tryFocusFirstName, 100)
    }
    void nextTick(() => window.setTimeout(tryFocusFirstName, 50))
  }

  if (props.autofocusEmail) {
    void nextTick(() => window.setTimeout(() => {
      if (document.activeElement && document.activeElement !== document.body) return
      focusProgrammatically(emailInput.value, { preventScroll: true })
    }, 50))
  }

  if (sectionRoot.value && 'IntersectionObserver' in window) {
    formViewObserver = new IntersectionObserver((entries) => {
      const visibleEntry = entries.find(entry => entry.isIntersecting)
      if (visibleEntry) {
        trackFormView(visibleEntry)
        formViewObserver?.disconnect()
        formViewObserver = null
      }
    }, { threshold: 0.35 })

    formViewObserver.observe(sectionRoot.value)
    return
  }

  trackFormView()
})

onBeforeUnmount(() => {
  if (socialFallbackTimer) clearTimeout(socialFallbackTimer)
  trackFormAbandonment('component_unmounted_after_form_start')
  window.removeEventListener('pagehide', handlePageHide)
  formViewObserver?.disconnect()
  formViewObserver = null
})
</script>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #000;
  -webkit-box-shadow: 0 0 0 1000px #f5efe4 inset;
  box-shadow: 0 0 0 1000px #f5efe4 inset;
  transition: background-color 9999s ease-in-out 0s;
}

.pa-field-enter-active {
  transition-property: opacity, transform;
  transition-duration: 420ms;
  transition-timing-function: ease;
}

.pa-field-leave-active {
  transition: opacity 150ms ease;
}

.pa-field-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.pa-field-leave-to {
  opacity: 0;
}

.private-access-input-placeholder {
  animation: private-access-placeholder 3.4s ease-in-out infinite;
}

@keyframes private-access-placeholder {
  0%,
  100% {
    opacity: 0.34;
    transform: translateY(-50%);
  }

  42% {
    opacity: 0.62;
    transform: translateY(-56%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .private-access-input-placeholder {
    animation: none;
  }
}

/* Custom chevron with room from the rounded border (native arrow sat on it). */
.private-access-select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-position: right 1.25rem center;
  background-repeat: no-repeat;
  background-size: 12px;
}
</style>
